import {ChangeDetectionStrategy, Component, computed, inject} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {ChartModule} from 'primeng/chart';
import {lastValueFrom} from 'rxjs';
import {DashboardService} from '../../../../../api';
import {ElciaCard} from '../../../../components/card/card';
import {Loader} from '../../../../components/loader/loader';
import {QueryKey} from '../../../../constants/query-key';
import {groupedBarOptions, readCssVariable} from '../../charts/chart-theme';
import {formatSeconds, toHours} from '../../dashboard.utils';

@Component({
  selector: 'elcia-dashboard-time-chart',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ChartModule, ElciaCard, Loader, TranslatePipe],
  templateUrl: './dashboard-time-chart.html',
  host: {class: 'block min-w-0'},
})
export class DashboardTimeChart {
  private readonly dashboardService = inject(DashboardService);
  private readonly translate = inject(TranslateService);

  // Same query key as the KPI tile: angular-query dedupes, this costs no extra request.
  private readonly statsQuery = injectQuery(() => ({
    queryKey: [QueryKey.DASHBOARD_TIMESHEET_STATS],
    queryFn: () => lastValueFrom(this.dashboardService.getDashboardTimesheetStats()),
  }));

  protected readonly isLoading = computed(() => this.statsQuery.isPending());
  private readonly slices = computed(() => this.statsQuery.data()?.stats?.weekSlices ?? []);
  protected readonly hasData = computed(() => this.slices().length > 0);

  protected readonly data = computed(() => {
    const slices = this.slices();
    const current = readCssVariable('--p-primary-500', '#3b82f6');
    const currentMuted = readCssVariable('--p-primary-200', '#bfdbfe');
    const previous = readCssVariable('--p-surface-300', '#cbd5e1');

    return {
      labels: slices.map(slice => `S${slice.index}`),
      datasets: [
        {
          label: this.translate.instant('dashboard.chart.previous-month'),
          data: slices.map(slice => toHours(slice.previousMonthSeconds)),
          backgroundColor: previous,
          borderRadius: 4,
        },
        {
          label: this.translate.instant('dashboard.chart.current-month'),
          // null travels through untouched: chart.js then draws no bar. Replacing it with 0 would say
          // "nothing done" where it must say "not started yet".
          data: slices.map(slice =>
            slice.currentMonthSeconds == null ? null : toHours(slice.currentMonthSeconds)),
          backgroundColor: slices.map(slice => (slice.isCurrent ? currentMuted : current)),
          borderRadius: 4,
        },
      ],
    };
  });

  protected readonly options = computed(() => {
    const slices = this.slices();
    const inProgress = this.translate.instant('dashboard.chart.in-progress');
    const base = groupedBarOptions(hours => formatSeconds(hours * 3600));

    return {
      ...base,
      plugins: {
        ...base.plugins,
        tooltip: {
          ...base.plugins.tooltip,
          callbacks: {
            ...base.plugins.tooltip.callbacks,
            afterLabel: (context: {dataIndex: number; datasetIndex: number}) =>
              context.datasetIndex === 1 && slices[context.dataIndex]?.isCurrent ? inProgress : '',
          },
        },
      },
    };
  });
}
